import { defineComponent } from 'vue';
import { createRouter, createWebHistory, type RouteRecordRaw, type Router } from 'vue-router';
import { resolveModules, getDefaultModuleId, type ModuleConfig } from '../config/moduleRegistry';
import { usePermissionsStore } from '../stores/permissionsStore';

declare module 'vue-router' {
  interface RouteMeta {
    moduleId?: string;
    name?: string;
    slug?: string;
    tabs?: { slug: string; name: string }[];
  }
}

/** The tenant-configured default module when permitted, else the first permitted module. */
export function pickDefaultModule(
  modules: ModuleConfig[],
  defaultId: string | null | undefined,
  hasPermission: (moduleId: string) => boolean,
): ModuleConfig | undefined {
  const configured = defaultId
    ? modules.find((m) => m.id === defaultId && hasPermission(m.moduleId))
    : undefined;
  return configured ?? modules.find((m) => hasPermission(m.moduleId));
}

/**
 * Where to go after permissions changed under an already-resolved route
 * (login, SSO handoff): from `/`, or from a module the new permissions do not
 * allow (e.g. a scoped SSO session replacing a full one), to the default
 * permitted module. Null = stay.
 */
export function rootRedirectTarget(
  currentPath: string,
  currentModuleId: string | undefined,
  modules: ModuleConfig[],
  defaultId: string | null | undefined,
  hasPermission: (moduleId: string) => boolean,
): string | null {
  const onRoot = currentPath === '/';
  const onForbiddenModule = !!currentModuleId && !hasPermission(currentModuleId);
  if (!onRoot && !onForbiddenModule) return null;
  const target = pickDefaultModule(modules, defaultId, hasPermission);
  return target ? `/${target.slug}` : null;
}

export function buildRoutes(modules: ModuleConfig[]): RouteRecordRaw[] {
  const seenSlugs = new Set<string>();
  const routes: RouteRecordRaw[] = [];

  for (const mod of modules) {
    if (!mod.slug) {
      throw new Error(`Module "${mod.id}" is missing required slug`);
    }
    if (seenSlugs.has(mod.slug)) {
      throw new Error(`Duplicate slug "${mod.slug}" — modules must have unique slugs`);
    }
    seenSlugs.add(mod.slug);

    const meta = {
      moduleId: mod.moduleId,
      name: mod.name,
      slug: mod.slug,
      tabs: mod.tabs ?? [],
    } satisfies import('vue-router').RouteMeta;

    // Resolve the component once. componentLoader returns a
    // defineAsyncComponent-wrapped component (synchronous). Passing the
    // function form to vue-router triggers a production-only "a.then is
    // not a function" because vue-router calls rawComponent() and then
    // .then(...) on the result without wrapping in Promise.resolve in
    // prod builds. Resolving up-front means vue-router takes the
    // isRouteComponent branch and never calls .then.
    const component = mod.componentLoader();

    if (!mod.tabs?.length) {
      routes.push({
        path: `/${mod.slug}`,
        name: mod.id,
        component,
        meta,
      });
    } else {
      routes.push({
        path: `/${mod.slug}`,
        redirect: `/${mod.slug}/${mod.tabs[0].slug}`,
      });
      routes.push({
        path: `/${mod.slug}/:tab`,
        name: mod.id,
        component,
        meta,
      });
    }
  }

  return routes;
}

/**
 * Holds the singleton Router instance. Populated by createAppRouter().
 * Exported so non-component code (Pinia stores) can read currentRoute / push
 * without needing Vue setup context. Undefined until createAppRouter() runs;
 * importing modules at app startup must not read this at module-eval time.
 */
export let router: Router;

export function createAppRouter(): Router {
  const moduleRoutes = buildRoutes(resolveModules());

  router = createRouter({
    history: createWebHistory(),
    routes: [
      ...moduleRoutes,
      // Default route: placeholder no-op; the guard below rewrites '/' to the
      // first module the user has permission for.
      { path: '/', name: '__root', component: defineComponent({ render: () => null }) },
      // Legacy /time-phase/* URLs redirect to the new sub-tabs under
      // /production-scheduler. Keeps bookmarks working after the
      // time-phase module was folded in.
      { path: '/time-phase',         redirect: '/production-scheduler/jobs' },
      { path: '/time-phase/jobs',    redirect: '/production-scheduler/jobs' },
      { path: '/time-phase/parts',   redirect: '/production-scheduler/jobs' },
      { path: '/time-phase/:any(.*)', redirect: '/production-scheduler/jobs' },
      // Catch-all: send unmatched paths to root, which re-runs the
      // default-pick guard. String form avoids the function-redirect
      // path that interacts poorly with Pinia devtools wrappers in
      // production builds.
      { path: '/:pathMatch(.*)*', redirect: '/' },
    ],
  });

  // Permission + default-pick guard. Marked async so Vue Router always
  // receives a real Promise — some plugin combinations (Pinia + Vue
  // devtools wrapper in production builds) mis-wrap a sync return such
  // that the guard's resolved value lacks a `.then`, surfacing as
  // "a.then is not a function" inside Vue Router's pushWithRedirect.
  router.beforeEach(async (to) => {
    const perms = usePermissionsStore();

    // Ensure permissions are loaded before any permission check below.
    // fetchUserPermissions() is idempotent (gated by isInitialized), so
    // subsequent guard invocations are no-ops. Without this await, a
    // direct-URL refresh races: the guard sees empty permissions, treats
    // the target module as unpermitted, and redirects to '/' → the
    // configured defaultModule, instead of staying on the requested route.
    await perms.fetchUserPermissions();

    // Default-picker. Prefer the tenant-configured `defaultModule` when set
    // and permitted; fall through to the first permitted module otherwise.
    if (to.path === '/') {
      const target = pickDefaultModule(resolveModules(), getDefaultModuleId(), (id) => perms.hasPermission(id));
      if (!target) return false; // No permitted modules — block navigation
      return { path: `/${target.slug}` };
    }

    // Module permission check
    const meta = to.meta;
    if (meta?.moduleId && !perms.hasPermission(meta.moduleId)) {
      return { path: '/' };
    }

    // Tab validation: if route declares tabs and the URL tab isn't one of them, redirect to first
    if (meta?.tabs?.length && to.params.tab) {
      const valid = meta.tabs.some((t) => t.slug === to.params.tab);
      if (!valid) {
        return { path: `/${meta.slug}/${meta.tabs[0].slug}` };
      }
    }

    return true;
  });

  // Page title
  router.afterEach((to) => {
    const meta = to.meta;
    if (!meta?.name) return;
    const tabSlug = to.params.tab as string | undefined;
    const tabName = meta.tabs?.find((t) => t.slug === tabSlug)?.name;
    document.title = tabName ? `${meta.name} — ${tabName}` : meta.name;
  });

  return router;
}
